import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';

// ─── Data ────────────────────────────────────────────────────────────────────

const colors = [
  {
    group: 'Primary',
    swatches: [
      { name: 'Charcoal', hex: '#2C2C2C', rgb: '44, 44, 44', usage: 'Primary text, buttons, headings', dark: true },
      { name: 'Ink', hex: '#1A1A1A', rgb: '26, 26, 26', usage: 'Deep backgrounds, hover states', dark: true },
      { name: 'White', hex: '#FFFFFF', rgb: '255, 255, 255', usage: 'Page backgrounds, card fills', dark: false },
    ],
  },
  {
    group: 'Gold Accent',
    swatches: [
      { name: 'Gold', hex: '#C9A96E', rgb: '201, 169, 110', usage: 'Accent highlights, active states', dark: false },
      { name: 'Warm Brown', hex: '#8B7355', rgb: '139, 115, 85', usage: 'Secondary text, links, labels', dark: true },
    ],
  },
  {
    group: 'Neutral',
    swatches: [
      { name: 'Cream', hex: '#F7F4EF', rgb: '247, 244, 239', usage: 'Page backgrounds, section fills', dark: false },
      { name: 'Linen', hex: '#F5F3F0', rgb: '245, 243, 240', usage: 'Footer background, subtle fills', dark: false },
      { name: 'Stone', hex: '#E8E6E3', rgb: '232, 230, 227', usage: 'Borders, dividers, card edges', dark: false },
    ],
  },
  {
    group: 'Text',
    swatches: [
      { name: 'Body', hex: '#6B6B6B', rgb: '107, 107, 107', usage: 'Body copy, descriptions', dark: true },
      { name: 'Muted', hex: '#9A9A9A', rgb: '154, 154, 154', usage: 'Captions, metadata, placeholders', dark: false },
      { name: 'Subtle', hex: '#4A4A4A', rgb: '74, 74, 74', usage: 'Secondary headings, subtext', dark: true },
    ],
  },
];

const typefaces = [
  {
    name: 'Cormorant Garamond',
    role: 'Display / Headings',
    weights: ['Light 300', 'Regular 400', 'Medium 500', 'SemiBold 600', 'Bold 700'],
    usage: 'All headings, the HSG monogram, hero text, section titles, pull quotes.',
    sample: 'Higher Standard Global',
    sampleSize: 'text-4xl',
    fontClass: "font-['Cormorant_Garamond']",
    note: 'Always use generous letter-spacing (tracking-wide or wider) for display use.',
  },
  {
    name: 'Inter',
    role: 'Body / UI',
    weights: ['Light 300', 'Regular 400', 'Medium 500', 'SemiBold 600'],
    usage: 'Body copy, navigation, buttons, labels, captions, form fields.',
    sample: 'Premium rental management for select clientele.',
    sampleSize: 'text-base',
    fontClass: "font-['Inter']",
    note: 'Keep body text at 14–16px. Use Light 300 for elegant secondary text.',
  },
];

const typescale = [
  { label: 'Display', size: 'text-5xl', weight: 'font-light', tracking: 'tracking-wide', font: "font-['Cormorant_Garamond']", sample: 'By Reference Only', usage: 'Hero headlines' },
  { label: 'H1', size: 'text-4xl', weight: 'font-light', tracking: 'tracking-wide', font: "font-['Cormorant_Garamond']", sample: 'Our Philosophy', usage: 'Page titles' },
  { label: 'H2', size: 'text-3xl', weight: 'font-light', tracking: 'tracking-normal', font: "font-['Cormorant_Garamond']", sample: 'Property Management', usage: 'Section headings' },
  { label: 'H3', size: 'text-xl', weight: 'font-medium', tracking: 'tracking-wide', font: "font-['Cormorant_Garamond']", sample: 'Lease Management', usage: 'Card titles, sub-sections' },
  { label: 'Label', size: 'text-xs', weight: 'font-medium', tracking: 'tracking-[0.3em]', font: "font-['Inter']", sample: 'HIGHER STANDARD GLOBAL', usage: 'Eyebrow labels (always uppercase)' },
  { label: 'Body', size: 'text-sm', weight: 'font-light', tracking: 'tracking-normal', font: "font-['Inter']", sample: 'We manage properties with discretion and precision for a select group of owners.', usage: 'Paragraphs, descriptions' },
  { label: 'Caption', size: 'text-xs', weight: 'font-normal', tracking: 'tracking-wide', font: "font-['Inter']", sample: 'By reference only · Est. 2018', usage: 'Metadata, footnotes, captions' },
];

const spacingScale = [
  { token: '4px', tailwind: 'p-1 / gap-1', usage: 'Icon padding, tight inline gaps' },
  { token: '8px', tailwind: 'p-2 / gap-2', usage: 'Button icon gap, badge padding' },
  { token: '12px', tailwind: 'p-3 / gap-3', usage: 'Input padding, compact list items' },
  { token: '16px', tailwind: 'p-4 / gap-4', usage: 'Card inner padding, nav item gap' },
  { token: '24px', tailwind: 'p-6 / gap-6', usage: 'Large card padding, column gap' },
  { token: '32px', tailwind: 'p-8 / gap-8', usage: 'Section inner padding, card gap' },
  { token: '48px', tailwind: 'py-12', usage: 'Section vertical rhythm (mobile)' },
  { token: '64px', tailwind: 'py-16', usage: 'Section vertical rhythm (desktop)' },
  { token: '96px', tailwind: 'py-24', usage: 'Hero vertical padding' },
];

const radiusScale = [
  { name: 'None', class: 'rounded-none', usage: 'Dividers, full-bleed images' },
  { name: 'Small', class: 'rounded-md', usage: 'Buttons, form inputs, badges' },
  { name: 'Default', class: 'rounded-lg', usage: 'Cards, panels, modals' },
  { name: 'Large', class: 'rounded-xl', usage: 'Feature cards, image containers' },
  { name: 'Full', class: 'rounded-full', usage: 'Pills, avatars, tags' },
];

const logoUsage = [
  { rule: 'Do', items: ['Use on white, cream, or dark backgrounds', 'Maintain clear space equal to the cap-height of "H" on all sides', 'Use the Gold on Dark variant for dark backgrounds', 'Use the Dark on White variant for light backgrounds'] },
  { rule: "Don't", items: ['Stretch or distort the logo', 'Place on busy photographic backgrounds without an overlay', 'Use colors outside the approved palette', 'Recreate the logo in a different typeface'] },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs uppercase tracking-[0.3em] text-[#8B7355] mb-3 font-medium">
      {children}
    </p>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#1A1A1A] mb-2">
      {children}
    </h2>
  );
}

function Divider() {
  return <div className="w-full h-px bg-[#E8E6E3] my-16" />;
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-all cursor-pointer whitespace-nowrap ${
        copied
          ? 'bg-[#4A7C59]/10 text-[#4A7C59]'
          : 'bg-[#F7F4EF] text-[#9A9A9A] hover:text-[#2C2C2C] hover:bg-[#E8E6E3]'
      }`}
    >
      <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`}></i>
      {copied ? 'Copied' : label}
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BrandPage() {
  useSEO({
    title: 'Brand Guidelines | Higher Standard Global',
    description: 'Official HSG brand reference — color palette, typography, spacing, and logo usage.',
    canonical: '/brand',
    noIndex: true,
  });

  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('colors');

  const sections = [
    { id: 'colors', label: 'Color Palette' },
    { id: 'typography', label: 'Typography' },
    { id: 'spacing', label: 'Spacing & Radius' },
    { id: 'logo', label: 'Logo Usage' },
    { id: 'social', label: 'Social Sharing' },
  ];

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#F7F4EF]">

      {/* ── Hero ── */}
      <div className="w-full bg-[#1A1A1A] pt-24 pb-16 px-6 md:px-16">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.35em] text-[#C9A96E] mb-4 font-medium">Higher Standard Global</p>
          <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-light text-white mb-4 leading-tight">
            Brand Guidelines
          </h1>
          <p className="text-sm text-white/50 max-w-lg leading-relaxed">
            The official reference for color, typography, spacing, and logo usage across all Higher Standard Global materials.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <a
              href="/logo-export"
              onClick={(e) => { e.preventDefault(); navigate('/logo-export'); }}
              className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-md bg-[#C9A96E] text-[#1A1A1A] font-medium hover:bg-[#B8956A] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line"></i>
              Logo Export Tool
            </a>
            <a
              href="/business-card"
              onClick={(e) => { e.preventDefault(); navigate('/business-card'); }}
              className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-md border border-white/20 text-white/70 hover:border-white/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-contacts-line"></i>
              Business Card
            </a>
            <a
              href="/letterhead"
              onClick={(e) => { e.preventDefault(); navigate('/letterhead'); }}
              className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-md border border-white/20 text-white/70 hover:border-white/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-text-line"></i>
              Letterhead
            </a>
            <a
              href="/brand-assets"
              onClick={(e) => { e.preventDefault(); navigate('/brand-assets'); }}
              className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-md border border-white/20 text-white/70 hover:border-white/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-layout-grid-line"></i>
              Brand Assets Hub
            </a>
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
              className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-md border border-white/20 text-white/70 hover:border-white/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to site
            </a>
          </div>
        </div>
      </div>

      {/* ── Sticky Nav ── */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#E8E6E3]">
        <div className="max-w-5xl mx-auto px-6 md:px-16">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                  activeSection === s.id
                    ? 'bg-[#2C2C2C] text-white'
                    : 'text-[#6B6B6B] hover:text-[#2C2C2C] hover:bg-[#F7F4EF]'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-5xl mx-auto px-6 md:px-16 py-16">

        {/* ════ COLOR PALETTE ════ */}
        <section id="colors">
          <SectionLabel>01 — Color System</SectionLabel>
          <SectionTitle>Color Palette</SectionTitle>
          <p className="text-sm text-[#6B6B6B] leading-relaxed mb-10 max-w-xl">
            The HSG palette is built on warm neutrals anchored by charcoal and elevated with a refined gold accent. Every color has a specific role — avoid mixing outside these combinations.
          </p>

          <div className="space-y-10">
            {colors.map((group) => (
              <div key={group.group}>
                <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">{group.group}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.swatches.map((swatch) => (
                    <div key={swatch.hex} className="bg-white rounded-lg border border-[#E8E6E3] overflow-hidden">
                      {/* Swatch */}
                      <div
                        className="h-24 w-full flex items-end p-3"
                        style={{ backgroundColor: swatch.hex }}
                      >
                        <span
                          className="text-xs font-mono tracking-wide"
                          style={{ color: swatch.dark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.4)' }}
                        >
                          {swatch.hex}
                        </span>
                      </div>
                      {/* Info */}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm font-medium text-[#1A1A1A]">{swatch.name}</p>
                          <CopyButton text={swatch.hex} label="HEX" />
                        </div>
                        <p className="text-[11px] text-[#9A9A9A] font-mono mb-2">rgb({swatch.rgb})</p>
                        <p className="text-xs text-[#6B6B6B] leading-relaxed">{swatch.usage}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Color combinations */}
          <div className="mt-10">
            <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">Approved Combinations</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { bg: '#FFFFFF', text: '#2C2C2C', accent: '#8B7355', label: 'Light — Primary', border: true },
                { bg: '#F7F4EF', text: '#2C2C2C', accent: '#8B7355', label: 'Light — Cream', border: false },
                { bg: '#1A1A1A', text: '#FFFFFF', accent: '#C9A96E', label: 'Dark — Primary', border: false },
                { bg: '#2C2C2C', text: '#C9A96E', accent: '#FFFFFF', label: 'Dark — Gold', border: false },
              ].map((combo) => (
                <div
                  key={combo.label}
                  className={`rounded-lg p-5 ${combo.border ? 'border border-[#E8E6E3]' : ''}`}
                  style={{ backgroundColor: combo.bg }}
                >
                  <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: combo.accent }}>
                    Higher Standard
                  </p>
                  <p className="text-sm font-medium mb-1" style={{ color: combo.text }}>
                    Global
                  </p>
                  <p className="text-[10px] mt-3 font-medium" style={{ color: combo.accent }}>
                    {combo.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Divider />

        {/* ════ TYPOGRAPHY ════ */}
        <section id="typography">
          <SectionLabel>02 — Typography</SectionLabel>
          <SectionTitle>Type System</SectionTitle>
          <p className="text-sm text-[#6B6B6B] leading-relaxed mb-10 max-w-xl">
            Two typefaces define the HSG voice: Cormorant Garamond for elegance and presence, Inter for clarity and precision. Never substitute these with system fonts in branded materials.
          </p>

          {/* Typeface cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {typefaces.map((tf) => (
              <div key={tf.name} className="bg-white rounded-lg border border-[#E8E6E3] p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#8B7355] mb-1">{tf.role}</p>
                    <p className="text-sm font-medium text-[#1A1A1A]">{tf.name}</p>
                  </div>
                  <CopyButton text={tf.name} label="Name" />
                </div>
                {/* Sample */}
                <div className="bg-[#FAFAF8] rounded-md p-5 mb-4 border border-[#E8E6E3]">
                  <p className={`${tf.fontClass} ${tf.sampleSize} text-[#1A1A1A] leading-snug`}>
                    {tf.sample}
                  </p>
                </div>
                {/* Weights */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {tf.weights.map((w) => (
                    <span key={w} className="text-[10px] px-2 py-1 bg-[#F7F4EF] text-[#6B6B6B] rounded-full border border-[#E8E6E3]">
                      {w}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-[#6B6B6B] leading-relaxed mb-2">{tf.usage}</p>
                <p className="text-[11px] text-[#9A9A9A] italic">{tf.note}</p>
              </div>
            ))}
          </div>

          {/* Type scale */}
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">Type Scale</p>
          <div className="bg-white rounded-lg border border-[#E8E6E3] overflow-hidden">
            {typescale.map((row, i) => (
              <div
                key={row.label}
                className={`flex items-center gap-6 px-6 py-5 ${i < typescale.length - 1 ? 'border-b border-[#E8E6E3]' : ''}`}
              >
                <div className="w-16 flex-shrink-0">
                  <span className="text-[10px] uppercase tracking-widest text-[#9A9A9A] font-medium">{row.label}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`${row.font} ${row.size} ${row.weight} ${row.tracking} text-[#1A1A1A] leading-snug truncate`}>
                    {row.sample}
                  </p>
                </div>
                <div className="hidden md:block w-40 flex-shrink-0 text-right">
                  <p className="text-[10px] text-[#9A9A9A]">{row.usage}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Google Fonts import */}
          <div className="mt-6 bg-[#1A1A1A] rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-white/50 uppercase tracking-widest">Google Fonts Import</p>
              <CopyButton
                text={`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');`}
                label="Copy CSS"
              />
            </div>
            <pre className="text-[11px] text-[#C9A96E] font-mono leading-relaxed whitespace-pre-wrap break-all">
              {`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');`}
            </pre>
          </div>
        </section>

        <Divider />

        {/* ════ SPACING & RADIUS ════ */}
        <section id="spacing">
          <SectionLabel>03 — Layout</SectionLabel>
          <SectionTitle>Spacing &amp; Radius</SectionTitle>
          <p className="text-sm text-[#6B6B6B] leading-relaxed mb-10 max-w-xl">
            Consistent spacing creates visual rhythm. HSG uses an 8px base grid. All spacing values are multiples of 4px, with 8px as the smallest meaningful unit.
          </p>

          {/* Spacing scale */}
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">Spacing Scale</p>
          <div className="bg-white rounded-lg border border-[#E8E6E3] overflow-hidden mb-10">
            {spacingScale.map((row, i) => (
              <div
                key={row.token}
                className={`flex items-center gap-6 px-6 py-4 ${i < spacingScale.length - 1 ? 'border-b border-[#E8E6E3]' : ''}`}
              >
                {/* Visual bar */}
                <div className="w-32 flex-shrink-0 flex items-center gap-3">
                  <div
                    className="bg-[#C9A96E]/30 rounded-sm flex-shrink-0"
                    style={{ width: Math.min(parseInt(row.token) * 1.2, 96), height: 16 }}
                  />
                </div>
                <div className="w-12 flex-shrink-0">
                  <span className="text-xs font-mono text-[#2C2C2C] font-medium">{row.token}</span>
                </div>
                <div className="flex-1">
                  <code className="text-[11px] text-[#8B7355] bg-[#F7F4EF] px-2 py-0.5 rounded font-mono whitespace-nowrap">{row.tailwind}</code>
                </div>
                <div className="hidden md:block w-48 text-right">
                  <p className="text-xs text-[#9A9A9A]">{row.usage}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Border radius */}
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">Border Radius</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {radiusScale.map((r) => (
              <div key={r.name} className="bg-white rounded-lg border border-[#E8E6E3] p-4 flex flex-col items-center gap-3">
                <div
                  className={`w-14 h-14 bg-[#2C2C2C] ${r.class}`}
                />
                <div className="text-center">
                  <p className="text-xs font-medium text-[#1A1A1A] mb-0.5">{r.name}</p>
                  <code className="text-[10px] text-[#8B7355] bg-[#F7F4EF] px-1.5 py-0.5 rounded font-mono whitespace-nowrap">{r.class}</code>
                  <p className="text-[10px] text-[#9A9A9A] mt-1.5 leading-snug">{r.usage}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Grid system */}
          <div className="mt-10 bg-white rounded-lg border border-[#E8E6E3] p-6">
            <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">Grid &amp; Max-Width</p>
            <div className="space-y-3">
              {[
                { label: 'Content max-width', value: 'max-w-5xl (1024px)', note: 'Standard page content container' },
                { label: 'Wide max-width', value: 'max-w-6xl (1152px)', note: 'Full-bleed sections, hero areas' },
                { label: 'Narrow max-width', value: 'max-w-lg (512px)', note: 'Text blocks, form containers' },
                { label: 'Page padding (desktop)', value: 'px-16 (64px)', note: 'Horizontal page gutter' },
                { label: 'Page padding (mobile)', value: 'px-6 (24px)', note: 'Mobile horizontal gutter' },
                { label: 'Section gap', value: 'py-16 / py-24', note: 'Vertical space between sections' },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between py-2 border-b border-[#F7F4EF] last:border-0">
                  <p className="text-xs text-[#6B6B6B]">{item.label}</p>
                  <div className="flex items-center gap-3">
                    <p className="text-[10px] text-[#9A9A9A] hidden sm:block">{item.note}</p>
                    <code className="text-[11px] text-[#8B7355] bg-[#F7F4EF] px-2 py-0.5 rounded font-mono whitespace-nowrap">{item.value}</code>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Divider />

        {/* ════ LOGO USAGE ════ */}
        <section id="logo">
          <SectionLabel>04 — Logo</SectionLabel>
          <SectionTitle>Logo Usage</SectionTitle>
          <p className="text-sm text-[#6B6B6B] leading-relaxed mb-10 max-w-xl">
            The HSG identity consists of the monogram and wordmark used together. Maintain the approved color combinations and clear space rules at all times.
          </p>

          {/* Logo variants showcase */}
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">Approved Variants</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            {[
              { bg: 'bg-white border border-[#E8E6E3]', mono: 'text-[#2C2C2C]', wm1: 'text-[#2C2C2C]', wm2: 'text-[#8B7355]', div: 'bg-[#2C2C2C]/20', label: 'Dark on White', preferred: true },
              { bg: 'bg-[#F7F4EF]', mono: 'text-[#2C2C2C]', wm1: 'text-[#2C2C2C]', wm2: 'text-[#8B7355]', div: 'bg-[#2C2C2C]/20', label: 'Dark on Cream', preferred: false },
              { bg: 'bg-[#1A1A1A]', mono: 'text-white', wm1: 'text-white', wm2: 'text-[#C9A96E]', div: 'bg-white/30', label: 'Light on Dark', preferred: false },
              { bg: 'bg-[#2C2C2C]', mono: 'text-[#C9A96E]', wm1: 'text-[#C9A96E]', wm2: 'text-white', div: 'bg-[#C9A96E]/30', label: 'Gold on Dark', preferred: true },
            ].map((v) => (
              <div key={v.label} className={`rounded-lg p-8 flex flex-col gap-4 ${v.bg}`}>
                {/* Logo */}
                <div className="flex items-center gap-4">
                  <span className={`font-['Cormorant_Garamond'] font-semibold tracking-widest text-3xl leading-none ${v.mono}`}>
                    HSG
                  </span>
                  <div className={`w-px h-8 ${v.div}`}></div>
                  <div className="flex flex-col gap-1">
                    <span className={`uppercase tracking-[0.22em] font-medium text-[10px] leading-none ${v.wm1}`}>Higher Standard</span>
                    <span className={`uppercase tracking-[0.35em] font-light text-[10px] leading-none ${v.wm2}`}>Global</span>
                  </div>
                </div>
                {/* Label */}
                <div className="flex items-center gap-2">
                  <p className={`text-[10px] uppercase tracking-widest ${v.mono} opacity-50`}>{v.label}</p>
                  {v.preferred && (
                    <span className="text-[9px] uppercase tracking-widest text-[#C9A96E] bg-[#C9A96E]/10 px-1.5 py-0.5 rounded-full border border-[#C9A96E]/20">
                      Preferred
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Clear space */}
          <div className="bg-white rounded-lg border border-[#E8E6E3] p-6 mb-6">
            <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">Clear Space Rule</p>
            <div className="flex items-center justify-center py-8 bg-[#FAFAF8] rounded-md border border-[#E8E6E3] mb-4">
              <div className="relative">
                {/* Clear space indicators */}
                <div className="border border-dashed border-[#C9A96E]/40 p-6 rounded">
                  <div className="flex items-center gap-4">
                    <span className="font-['Cormorant_Garamond'] font-semibold tracking-widest text-3xl text-[#2C2C2C]">HSG</span>
                    <div className="w-px h-8 bg-[#2C2C2C]/20"></div>
                    <div className="flex flex-col gap-1">
                      <span className="uppercase tracking-[0.22em] font-medium text-[10px] text-[#2C2C2C]">Higher Standard</span>
                      <span className="uppercase tracking-[0.35em] font-light text-[10px] text-[#8B7355]">Global</span>
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-[#C9A96E] text-center mt-2">← minimum clear space (cap-height of "H") →</p>
              </div>
            </div>
            <p className="text-xs text-[#6B6B6B] leading-relaxed">
              Always maintain a minimum clear space equal to the cap-height of the "H" in the monogram on all four sides of the logo. No other graphic elements, text, or imagery should appear within this zone.
            </p>
          </div>

          {/* Do / Don't */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {logoUsage.map((block) => (
              <div
                key={block.rule}
                className={`rounded-lg border p-6 ${
                  block.rule === 'Do'
                    ? 'border-[#4A7C59]/30 bg-[#4A7C59]/5'
                    : 'border-[#C0392B]/20 bg-[#C0392B]/5'
                }`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-5 h-5 flex items-center justify-center rounded-full ${block.rule === 'Do' ? 'bg-[#4A7C59]' : 'bg-[#C0392B]'}`}>
                    <i className={`text-white text-xs ${block.rule === 'Do' ? 'ri-check-line' : 'ri-close-line'}`}></i>
                  </div>
                  <p className={`text-sm font-medium ${block.rule === 'Do' ? 'text-[#4A7C59]' : 'text-[#C0392B]'}`}>
                    {block.rule}
                  </p>
                </div>
                <ul className="space-y-2">
                  {block.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-[#6B6B6B] leading-relaxed">
                      <i className={`mt-0.5 flex-shrink-0 text-xs ${block.rule === 'Do' ? 'ri-checkbox-circle-line text-[#4A7C59]' : 'ri-close-circle-line text-[#C0392B]'}`}></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ════ SOCIAL SHARING ════ */}
        <section id="social">
          <SectionLabel>05 — Social Sharing</SectionLabel>
          <SectionTitle>Open Graph Image</SectionTitle>
          <p className="text-sm text-[#6B6B6B] leading-relaxed mb-10 max-w-xl">
            When the site is shared on social media, messaging apps, or linked in emails, this branded preview card appears. It uses the standard 1200×630px Open Graph format.
          </p>

          {/* OG image preview */}
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">Preview Image (1200 × 630)</p>
          <div className="bg-white rounded-lg border border-[#E8E6E3] overflow-hidden mb-8">
            <div className="w-full aspect-[1200/630] overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/15c70b67a8fcda6e18a7a735ab2ecbf7.jpg"
                alt="HSG Open Graph preview"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Platform previews */}
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">How it looks on each platform</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">

            {/* Twitter / X card */}
            <div className="bg-white rounded-lg border border-[#E8E6E3] p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-twitter-x-line text-sm text-[#1A1A1A]"></i>
                <p className="text-xs font-medium text-[#1A1A1A]">X (Twitter) — Large Card</p>
              </div>
              <div className="rounded-xl overflow-hidden border border-[#E8E6E3]">
                <div className="w-full aspect-[1200/630] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=luxury%20premium%20property%20management%20brand%20identity%20dark%20charcoal%20background%20with%20warm%20gold%20accent%20typography%20elegant%20minimalist%20real%20estate%20branding%20card%20wide%20format%20professional%20high%20end%20residential%20properties%20sophisticated%20dark%20tone%20with%20golden%20serif%20text%20overlay%20architectural%20detail&width=1200&height=630&seq=hsg-og-001&orientation=landscape"
                    alt="Twitter card preview"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="px-3 py-2.5 bg-white border-t border-[#E8E6E3]">
                  <p className="text-[11px] text-[#9A9A9A] mb-0.5">higherstandardglobal.com</p>
                  <p className="text-xs font-medium text-[#1A1A1A] leading-snug">Higher Standard Global | Premium Rental Property Management</p>
                  <p className="text-[11px] text-[#6B6B6B] mt-0.5 leading-snug">Curated property management for discerning owners. By reference only.</p>
                </div>
              </div>
            </div>

            {/* LinkedIn / Facebook card */}
            <div className="bg-white rounded-lg border border-[#E8E6E3] p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-linkedin-box-line text-sm text-[#1A1A1A]"></i>
                <p className="text-xs font-medium text-[#1A1A1A]">LinkedIn / Facebook</p>
              </div>
              <div className="rounded-lg overflow-hidden border border-[#E8E6E3]">
                <div className="w-full aspect-[1200/630] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=luxury%20premium%20property%20management%20brand%20identity%20dark%20charcoal%20background%20with%20warm%20gold%20accent%20typography%20elegant%20minimalist%20real%20estate%20branding%20card%20wide%20format%20professional%20high%20end%20residential%20properties%20sophisticated%20dark%20tone%20with%20golden%20serif%20text%20overlay%20architectural%20detail&width=1200&height=630&seq=hsg-og-001&orientation=landscape"
                    alt="LinkedIn card preview"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="px-3 py-3 bg-[#F7F4EF] border-t border-[#E8E6E3]">
                  <p className="text-[10px] uppercase tracking-widest text-[#9A9A9A] mb-0.5">HIGHERSTANDARDGLOBAL.COM</p>
                  <p className="text-xs font-semibold text-[#1A1A1A] leading-snug">Higher Standard Global | Premium Rental Property Management</p>
                  <p className="text-[11px] text-[#6B6B6B] mt-0.5 leading-snug">Curated property management for discerning owners. By reference only.</p>
                </div>
              </div>
            </div>
          </div>

          {/* iMessage / WhatsApp preview */}
          <div className="bg-white rounded-lg border border-[#E8E6E3] p-5 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-message-2-line text-sm text-[#1A1A1A]"></i>
              <p className="text-xs font-medium text-[#1A1A1A]">iMessage / WhatsApp — Link Preview</p>
            </div>
            <div className="max-w-xs">
              <div className="rounded-xl overflow-hidden border border-[#E8E6E3] bg-[#F7F4EF]">
                <div className="w-full h-36 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=luxury%20premium%20property%20management%20brand%20identity%20dark%20charcoal%20background%20with%20warm%20gold%20accent%20typography%20elegant%20minimalist%20real%20estate%20branding%20card%20wide%20format%20professional%20high%20end%20residential%20properties%20sophisticated%20dark%20tone%20with%20golden%20serif%20text%20overlay%20architectural%20detail&width=1200&height=630&seq=hsg-og-001&orientation=landscape"
                    alt="iMessage preview"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="px-3 py-2.5">
                  <p className="text-[10px] text-[#9A9A9A] mb-0.5">higherstandardglobal.com</p>
                  <p className="text-xs font-medium text-[#1A1A1A] leading-snug">Higher Standard Global</p>
                  <p className="text-[11px] text-[#6B6B6B] leading-snug">Premium Rental Property Management</p>
                </div>
              </div>
            </div>
          </div>

          {/* Meta tags code block */}
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">Meta Tags in index.html</p>
          <div className="bg-[#1A1A1A] rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <p className="text-xs text-white/40 uppercase tracking-widest">HTML &lt;head&gt;</p>
              <CopyButton
                text={`<!-- Open Graph -->
<meta property="og:title" content="Higher Standard Global | Premium Rental Property Management" />
<meta property="og:description" content="Curated property management for discerning owners. By reference only." />
<meta property="og:type" content="website" />
<meta property="og:url" content="https://higherstandardglobal.com/" />
<meta property="og:image" content="https://higherstandardglobal.com/og-image.jpg" />
<meta property="og:image:width" content="1200" />
<meta property="og:image:height" content="630" />
<meta property="og:site_name" content="Higher Standard Global" />
<!-- Twitter / X Card -->
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:title" content="Higher Standard Global | Premium Rental Property Management" />
<meta name="twitter:description" content="Curated property management for discerning owners. By reference only." />
<meta name="twitter:image" content="https://higherstandardglobal.com/og-image.jpg" />`}
                label="Copy tags"
              />
            </div>
            <pre className="text-[11px] text-[#C9A96E] font-mono leading-relaxed p-5 overflow-x-auto whitespace-pre">{`<!-- Open Graph -->
<meta property="og:title" content="Higher Standard Global | Premium Rental Property Management" />
<meta property="og:description" content="Curated property management for discerning owners. By reference only." />
<meta property="og:type" content="website" />
<meta property="og:url" content="https://higherstandardglobal.com/" />
<meta property="og:image" content="https://higherstandardglobal.com/og-image.jpg" />
<meta property="og:image:width" content="1200" />
<meta property="og:image:height" content="630" />
<meta property="og:site_name" content="Higher Standard Global" />
<!-- Twitter / X Card -->
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:title" content="Higher Standard Global | Premium Rental Property Management" />
<meta name="twitter:description" content="Curated property management for discerning owners. By reference only." />
<meta name="twitter:image" content="https://higherstandardglobal.com/og-image.jpg" />`}</pre>
          </div>

          {/* Spec table */}
          <div className="mt-6 bg-white rounded-lg border border-[#E8E6E3] p-6">
            <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">OG Image Specifications</p>
            <div className="space-y-2">
              {[
                { label: 'Dimensions', value: '1200 × 630 px', note: 'Standard OG format' },
                { label: 'Aspect ratio', value: '1.91 : 1', note: 'Required by most platforms' },
                { label: 'Format', value: 'JPG or PNG', note: 'JPG recommended for photos' },
                { label: 'Max file size', value: '8 MB', note: 'Keep under 1 MB for fast loading' },
                { label: 'Safe zone', value: '1080 × 540 px centered', note: 'Keep key content within this area' },
                { label: 'Twitter min size', value: '300 × 157 px', note: 'For summary_large_image card' },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between py-2 border-b border-[#F7F4EF] last:border-0">
                  <p className="text-xs text-[#6B6B6B]">{row.label}</p>
                  <div className="flex items-center gap-3">
                    <p className="text-[10px] text-[#9A9A9A] hidden sm:block">{row.note}</p>
                    <code className="text-[11px] text-[#8B7355] bg-[#F7F4EF] px-2 py-0.5 rounded font-mono whitespace-nowrap">{row.value}</code>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Divider />

        {/* ── Footer CTA ── */}
        <div className="text-center pb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#8B7355] mb-3">Resources</p>
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1A1A] mb-4">
            Need the logo files?
          </h3>
          <p className="text-sm text-[#6B6B6B] mb-6 max-w-sm mx-auto leading-relaxed">
            Head to the Logo Export Tool to download SVGs, copy embed code, and generate favicons.
          </p>
          <a
            href="/logo-export"
            onClick={(e) => { e.preventDefault(); navigate('/logo-export'); }}
            className="inline-flex items-center gap-2 bg-[#2C2C2C] text-white text-xs px-6 py-3 rounded-md hover:bg-[#C9A96E] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-download-line"></i>
            Open Logo Export Tool
          </a>
        </div>

      </div>
    </div>
  );
}
